import { useCallback } from "react";
import { getCatalogSearch } from "@/api/client";
import type { CatalogSearchRow } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatNumber } from "@/lib/format";

const columns: Column<CatalogSearchRow>[] = [
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Check digit",
    cell: (r) => r.checkDigit ?? "—",
    sortValue: (r) => r.checkDigit,
  },
  {
    header: "Product name",
    cell: (r) => r.productName ?? "—",
    sortValue: (r) => r.productName,
  },
  {
    header: "Brand",
    cell: (r) => r.brandName ?? "—",
    sortValue: (r) => r.brandName,
  },
  {
    header: "Category",
    cell: (r) => r.categoryName ?? "—",
    sortValue: (r) => r.categoryName,
  },
  {
    header: "Subcategory",
    cell: (r) => r.subcategoryName ?? "—",
    sortValue: (r) => r.subcategoryName,
  },
];

export function CatalogSearchPage() {
  const filters = useProductFilterOptions();
  const [draft, setDraft, q] = useDebouncedUrlFilter("q");
  const [brandIds, setBrandIds, brand] = useUrlListFilter("brand");
  const [categoryIds, setCategoryIds, category] = useUrlListFilter("category");
  const [subcategoryIds, setSubcategoryIds, subcategory] =
    useUrlListFilter("subcategory");

  const fetchCatalog = useCallback(
    (token: string) =>
      getCatalogSearch(token, {
        q: q || undefined,
        brandId: brand,
        categoryId: category,
        subcategoryId: subcategory,
      }),
    [q, brand, category, subcategory],
  );
  const state = useApiData(fetchCatalog);
  const data = state.status === "ready" ? state.data : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Catalog search</h1>
        <p className="text-sm text-muted-foreground">
          Search the shared product catalog by name or UPC, and narrow by
          brand or category.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="catalog-search"
            className="text-xs font-medium text-muted-foreground"
          >
            Search
          </label>
          <input
            id="catalog-search"
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Product name or UPC"
            className="h-9 w-72 max-w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <MultiFilterSelect
          label="Brand"
          allLabel="All brands"
          values={brandIds}
          onChange={setBrandIds}
          disabled={filters.disabled}
          options={filters.brandOptions}
        />
        <MultiFilterSelect
          label="Category"
          allLabel="All categories"
          values={categoryIds}
          onChange={setCategoryIds}
          disabled={filters.disabled}
          options={filters.level1CategoryOptions}
        />
        <MultiFilterSelect
          label="Subcategory"
          allLabel="All subcategories"
          values={subcategoryIds}
          onChange={setSubcategoryIds}
          disabled={filters.disabled}
          options={filters.categoryOptions}
        />
      </div>

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Products</CardTitle>
            <CardDescription>
              {data?.capped
                ? `Showing the first ${formatNumber(data.rowLimit)} matches — narrow the search to see the rest.`
                : data
                  ? `${formatNumber(data.rows.length)} ${data.rows.length === 1 ? "product" : "products"}`
                  : "Global catalog (products without an owning account)"}
            </CardDescription>
          </div>
          {data ? (
            <CsvDownloadButton
              name="catalog-search"
              columns={columns}
              rows={data.rows}
            />
          ) : null}
        </CardHeader>
        <CardContent>
          {state.status === "loading" ? <TableSkeleton rows={10} /> : null}
          {state.status === "error" ? (
            <ErrorState message={state.message} onRetry={state.reload} />
          ) : null}
          {data ? (
            <DataTable
              columns={columns}
              rows={data.rows}
              rowKey={(r, i) => `${r.upc}-${i}`}
              maxHeight="36rem"
              emptyMessage="No products match"
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
